import { cva } from 'class-variance-authority'
import { type HTMLAttributes, type KeyboardEvent, type Key, type ReactNode, useState } from 'react'
import { Button as AriaButton, TextArea as AriaTextArea, TextField as AriaTextField } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { fieldFocusRing, focusRing } from '../../lib/focus-ring'
import { Avatar, type AvatarTintBySize } from '../../atoms/avatar/avatar'
import { Icon, type IconName } from '../../atoms/icon/icon'
import { Title } from '../../molecules/title/title'

/**
 * Comments (463:50857): a white card, 16px radius, 24px padding, drawn 764px
 * wide. A Medium Title without its arrow (the component turns `Arrow` off), then
 * 24px below the comments and the composer, 16px apart. Its `comment` boolean is
 * whether there is a comment to show.
 *
 * A comment is the 36.125px Comments avatar (initials in Mona Sans SemiExpanded), 8px from the author in SemiBold 16
 * (#292A2B), the time beside it in Regular 12 and the body in Regular 14, both in
 * #6A7282. The time and body sit on a 10.53px line as drawn, so a body that wraps
 * overlaps its own lines. The composer is a 110px Background box, 10px radius,
 * with the "Add comment" placeholder on top and the add, emoji and attachment
 * icons 11px apart at the bottom.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma;
 * the composer draws it while its field has keyboard focus, not on a click.
 */
export const commentsVariants = cva('flex flex-col items-start rounded-atlas-lg bg-atlas-light p-6 font-atlas-sans')

/** One comment in the thread. */
export interface CommentEntry {
	id: Key
	author: {
		name: string
		/** The initials drawn in the avatar, e.g. "ER". */
		initials: string
		tint?: AvatarTintBySize['comment']
	}
	/** When it was written, already formatted, e.g. "Hace 7 días". */
	time: string
	body: ReactNode
}

export interface CommentsProps extends Omit<HTMLAttributes<HTMLElement>, 'children' | 'title' | 'onSubmit'> {
	title?: string
	comments: CommentEntry[]
	/** Called with the trimmed draft when Enter is pressed (Shift+Enter adds a line); the field then clears. */
	onSubmit?: (text: string) => void
	placeholder?: string
	/** Accessible name of the composer field; defaults to the placeholder. */
	composerLabel?: string
	onAdd?: () => void
	onEmoji?: () => void
	onAttach?: () => void
	addLabel?: string
	emojiLabel?: string
	attachLabel?: string
}

const iconButtonClass = cn('inline-flex shrink-0 cursor-pointer rounded-atlas-sm', focusRing)

function IconAction({ icon, label, onPress }: { icon: IconName; label: string; onPress?: () => void }) {
	return (
		<AriaButton aria-label={label} onPress={onPress} className={iconButtonClass}>
			<Icon name={icon} />
		</AriaButton>
	)
}

function CommentItem({ author, time, body }: Omit<CommentEntry, 'id'>) {
	return (
		<article className="flex w-full items-start gap-2">
			<Avatar size="comment" initials={author.initials} tint={author.tint} aria-hidden="true" />
			<div className="flex min-w-0 flex-1 flex-col gap-2">
				<div className="flex w-full items-center gap-1">
					<span className="whitespace-nowrap text-atlas-comment-author text-atlas-avatar-ink">{author.name}</span>
					<span className="whitespace-nowrap text-atlas-comment-meta text-atlas-gray-500">{time}</span>
				</div>
				<div className="w-full text-atlas-comment-body text-atlas-gray-500">{body}</div>
			</div>
		</article>
	)
}

type ComposerProps = Pick<
	CommentsProps,
	| 'onSubmit'
	| 'placeholder'
	| 'composerLabel'
	| 'onAdd'
	| 'onEmoji'
	| 'onAttach'
	| 'addLabel'
	| 'emojiLabel'
	| 'attachLabel'
>

function Composer({ onSubmit, placeholder = 'Add comment', composerLabel, ...actions }: ComposerProps) {
	const [draft, setDraft] = useState('')
	const onKeyDown = (event: KeyboardEvent) => {
		if (event.key !== 'Enter' || event.shiftKey) return
		event.preventDefault()
		const text = draft.trim()
		if (!text) return
		onSubmit?.(text)
		setDraft('')
	}
	return (
		<AriaTextField
			aria-label={composerLabel ?? placeholder}
			value={draft}
			onChange={setDraft}
			className={cn(
				'flex h-27.5 w-full flex-col justify-between rounded-atlas-badge bg-atlas-background p-4',
				fieldFocusRing,
			)}
		>
			<AriaTextArea
				placeholder={placeholder}
				onKeyDown={onKeyDown}
				className="min-h-0 w-full flex-1 resize-none bg-transparent text-atlas-comment-placeholder text-atlas-dark outline-none placeholder:text-atlas-light-dark"
			/>
			<div className="flex items-center gap-2.75">
				<IconAction icon="add-01" label={actions.addLabel ?? 'Add'} onPress={actions.onAdd} />
				<IconAction icon="relieved-01" label={actions.emojiLabel ?? 'Add emoji'} onPress={actions.onEmoji} />
				<IconAction icon="attachment-01" label={actions.attachLabel ?? 'Attach file'} onPress={actions.onAttach} />
			</div>
		</AriaTextField>
	)
}

/**
 * A work order's comment thread and its composer. Presentational: the screen
 * owns the comments and posts the text `onSubmit` reports; the add, emoji and
 * attachment icons are callbacks.
 */
export function Comments({ title = 'Comments', comments, className, ...props }: CommentsProps) {
	const { onSubmit, placeholder, composerLabel, onAdd, onEmoji, onAttach, addLabel, emojiLabel, attachLabel, ...rest } =
		props
	return (
		<section {...rest} className={cn(commentsVariants(), className)}>
			<div className="flex w-full flex-col gap-6">
				<Title size="medium" title={title} arrow={false} />
				<div className="flex w-full flex-col gap-4">
					{comments.length > 0 ? (
						<ul className="flex w-full flex-col gap-4">
							{comments.map(({ id, ...comment }) => (
								<li key={id}>
									<CommentItem {...comment} />
								</li>
							))}
						</ul>
					) : null}
					<Composer
						{...{ onSubmit, placeholder, composerLabel, onAdd, onEmoji, onAttach, addLabel, emojiLabel, attachLabel }}
					/>
				</div>
			</div>
		</section>
	)
}
